type PerfOptions = {
  sid?: string;
  targetFps?: number;
  memorySamplingMs?: number;
  exportPeriodMs?: number;
  enableGcObserver?: boolean;
  enableLongTaskObserver?: boolean;
  enableHeavyCpu?: boolean;
  heavyCpuIntensity?: number;
};

type CycleRecord = {
  t: number;
  intervalMs: number;
  durationMs: number;
  fps: number;
  targetFps: number;
  score?: number;
  penalties?: number;
  hiddenMs?: number;
  visibility?: 'visible' | 'hidden' | 'prerender' | 'unloaded';
  minimized?: boolean;
  dropped?: boolean;
};

type CycleInput = {
  intervalMs?: number;
  durationMs: number;
  score?: number;
  penalties?: number;
  hiddenMs?: number;
};

type PerfResults = {
  startedAt: number;
  sid?: string;
  cycles: CycleRecord[];
  longTasks: Array<{ t: number; duration: number }>;
  gcs: Array<{ t: number; duration: number }>;
  memorySamples: Array<{ t: number; jsHeapUsed?: number; uaSpecificTotal?: number }>;
  stats: {
    avgIntervalMs: number;
    avgDurationMs: number;
    avgFps: number;
    droppedCount: number;
  };
};

const nowTs = () => Date.now();

export const initPerfMonitor = (opts: PerfOptions = {}) => {
  const sid = opts.sid;
  const targetFps = typeof opts.targetFps === 'number' ? opts.targetFps : 1;
  const results: PerfResults = {
    startedAt: nowTs(),
    sid,
    cycles: [],
    longTasks: [],
    gcs: [],
    memorySamples: [],
    stats: { avgIntervalMs: 0, avgDurationMs: 0, avgFps: 0, droppedCount: 0 },
  };

  let lastTickTs = 0;
  let exportTimer: number | null = null;
  let memTimer: number | null = null;
  let heavyWorker: Worker | null = null;

  const droppedThresholdMs = (1000 / Math.max(0.001, targetFps)) * 1.5;

  const supported = (typeof PerformanceObserver !== 'undefined' && (PerformanceObserver as any).supportedEntryTypes) ? (PerformanceObserver as any).supportedEntryTypes : [];
  const canLongTask = supported.includes('longtask');
  const canGc = supported.includes('gc');

  let longTaskObs: PerformanceObserver | null = null;
  let gcObs: PerformanceObserver | null = null;

  if (opts.enableLongTaskObserver !== false && canLongTask) {
    try {
      longTaskObs = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          results.longTasks.push({ t: nowTs(), duration: (entry as any).duration || 0 });
        }
      });
      longTaskObs.observe({ entryTypes: ['longtask'] as any });
    } catch {}
  }

  if (opts.enableGcObserver && canGc) {
    try {
      gcObs = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          results.gcs.push({ t: nowTs(), duration: (entry as any).duration || 0 });
        }
      });
      gcObs.observe({ entryTypes: ['gc'] as any });
    } catch {}
  }

  const sampleMemory = async () => {
    const t = nowTs();
    try {
      const uaMem = (performance as any).measureUserAgentSpecificMemory;
      if (typeof uaMem === 'function') {
        const m = await uaMem.call(performance);
        const total = (m && m.bytes && typeof m.bytes === 'number') ? m.bytes : undefined;
        results.memorySamples.push({ t, uaSpecificTotal: total });
        return;
      }
    } catch {}
    try {
      const pm = (performance as any).memory;
      if (pm && typeof pm.jsHeapUsedSize === 'number') {
        results.memorySamples.push({ t, jsHeapUsed: pm.jsHeapUsedSize });
      }
    } catch {}
  };

  const startMemoryTimer = () => {
    const ms = typeof opts.memorySamplingMs === 'number' ? Math.max(1000, opts.memorySamplingMs) : 10000;
    if (memTimer) (window as any).clearInterval(memTimer);
    memTimer = window.setInterval(() => {
      sampleMemory();
    }, ms);
  };

  const exportResults = () => {
    try {
      const key = sid ? `focusPerf:results:${sid}` : `focusPerf:results`;
      const out = {
        ...results,
        stats: computeStats(results.cycles),
      };
      localStorage.setItem(key, JSON.stringify(out));
      (window as any).__focusPerfResults = out;
    } catch {}
  };

  const startExportTimer = () => {
    const ms = typeof opts.exportPeriodMs === 'number' ? Math.max(2000, opts.exportPeriodMs) : 15000;
    if (exportTimer) (window as any).clearInterval(exportTimer);
    exportTimer = window.setInterval(() => {
      exportResults();
    }, ms);
  };

  const startHeavyCpu = () => {
    if (!opts.enableHeavyCpu) return;
    try {
      // const intensity = Math.max(0, Math.min(1, opts.heavyCpuIntensity ?? 0.75)); // unused
      const blob = new Blob([`
        let running = false;
        let targetMs = ${Math.floor(50 + 1950 * (opts.heavyCpuIntensity ?? 0.75))};
        function burn(ms) {
          const end = performance.now() + ms;
          while (performance.now() < end) {
            let x = 0;
            for (let i = 0; i < 10000; i++) x += Math.sqrt(i) * Math.sin(i);
          }
        }
        onmessage = (e) => {
          if (e.data === 'start') {
            running = true;
            const loop = () => {
              if (!running) return;
              burn(targetMs);
              setTimeout(loop, 10);
            };
            loop();
          } else if (e.data === 'stop') {
            running = false;
          } else if (typeof e.data === 'object' && e.data.type === 'intensity') {
            targetMs = Math.floor(50 + 1950 * (e.data.value || 0.75));
          }
        };
      `], { type: 'application/javascript' });
      heavyWorker = new Worker(URL.createObjectURL(blob));
      heavyWorker.postMessage('start');
    } catch {}
  };

  const stopHeavyCpu = () => {
    try { heavyWorker?.postMessage('stop'); } catch {}
    try { heavyWorker?.terminate?.(); } catch {}
    heavyWorker = null;
  };

  const computeStats = (cycles: CycleRecord[]) => {
    const n = cycles.length || 1;
    let sInt = 0, sDur = 0, sFps = 0, dropped = 0;
    for (const c of cycles) {
      sInt += c.intervalMs;
      sDur += c.durationMs;
      sFps += c.fps;
      if (c.dropped) dropped++;
    }
    return {
      avgIntervalMs: sInt / n,
      avgDurationMs: sDur / n,
      avgFps: sFps / n,
      droppedCount: dropped,
    };
  };

  const recordCycle = (partial: CycleInput) => {
    const t = nowTs();
    const vis = (typeof document !== 'undefined' ? (document.visibilityState as any) : 'visible');
    const minimized = (typeof document !== 'undefined') ? !document.hasFocus() : false;
    lastTickTs = lastTickTs === 0 ? t : lastTickTs;
    const intervalMs = typeof partial.intervalMs === 'number' ? partial.intervalMs : Math.max(1, t - lastTickTs);
    lastTickTs = t;
    const fps = 1000 / Math.max(1, intervalMs);
    const rec: CycleRecord = {
      t,
      targetFps,
      intervalMs,
      durationMs: Math.max(0, partial.durationMs || 0),
      fps,
      score: partial.score,
      penalties: partial.penalties,
      hiddenMs: partial.hiddenMs,
      visibility: vis,
      minimized,
      dropped: intervalMs > droppedThresholdMs,
    };
    results.cycles.push(rec);
  };

  const start = () => {
    startExportTimer();
    startMemoryTimer();
    startHeavyCpu();
    exportResults();
    try { (window as any).__focusPerfMonitorActive = true; } catch {}
  };
  const stop = () => {
    if (exportTimer) (window as any).clearInterval(exportTimer);
    if (memTimer) (window as any).clearInterval(memTimer);
    exportResults();
    stopHeavyCpu();
    try { (window as any).__focusPerfMonitorActive = false; } catch {}
    try { longTaskObs?.disconnect?.(); } catch {}
    try { gcObs?.disconnect?.(); } catch {}
  };

  // Auto-start if flagged
  try {
    const flag = localStorage.getItem('focusPerfTest:v1');
    if (flag === '1') start();
  } catch {}

  return {
    recordCycle,
    start,
    stop,
    getResults: () => ({ ...results, stats: computeStats(results.cycles) }),
  };
};
